import { cloneDeep, merge } from 'lodash';
import { KioskType } from '../kiosk/kiosk';

export interface ConfigSchema {
  analytics: {
    segmentKey: string;
  };
  api: {
    domain: string;
  };
  assets: {
    loggingTutorial: string;
  };
  kiosk: {
    enabled: boolean;
    type: KioskType;
  };
  env: string;
  files: {
    domain: string;
  };
  graphql: {
    uri: string;
  };
  intercom: {
    appId: string;
    articlesUrl: string;
  };
  proxy: {
    domain?: string;
  };
  public: {
    url?: string;
  };
  robot: {
    network: {
      host: string;
    };
    api: {
      path: string;
      port: number;
    };
    rpc: {
      host: string;
      path: string;
      port: number;
    };
  };
  simulate: {
    barcode: {
      serialNumber: string;
      softwareVersion: string;
      urCapVersion: string;
      firmwareVersion: string;
      manufacturer: string;
      model: string;
      threePositionEnabled: boolean;
    };
    wifi: {
      gateway: string;
      ipAddress: string;
    };
  };
  support: {
    email: string;
  };
}

const BaseApplicationConfig: ConfigSchema = {
  analytics: {
    segmentKey: import.meta.env.VITE_SEGMENT_KEY as string,
  },
  api: {
    domain: import.meta.env.VITE_API_DOMAIN as string,
  },
  assets: {
    loggingTutorial: 'https://bit.ly/34Jj0eV',
  },
  kiosk: {
    enabled: import.meta.env.VITE_KIOSK_ENABLED === 'true',
    type: KioskType.TEACH_PENDANT,
  },
  env: import.meta.env.VITE_ENV || 'dev',
  files: {
    domain: import.meta.env.VITE_FILES_DOMAIN as string,
  },
  graphql: {
    uri: `${import.meta.env.VITE_API_DOMAIN}/graphql`,
  },
  intercom: {
    appId: import.meta.env.VITE_INTERCOM_APP_ID as string,
    articlesUrl: `https://help.hirebotics.com/en/articles`,
  },
  proxy: {
    domain: import.meta.env.VITE_PROXY_DOMAIN,
  },
  public: {
    url: import.meta.env.BASE_URL,
  },
  robot: {
    network: {
      host: '127.0.0.1',
    },
    api: {
      // be sure to include the slash at the end since the construction of the
      // urls using new URL will truncate the last part of the path if it
      // doesn't end with a slash
      path: '/',
      port: 39800,
    },
    rpc: {
      host: '127.0.0.1',
      path: '/RPC2',
      port: 6968,
    },
  },
  simulate: {
    /**
     * Simulated barcode data for the machine registration process.
     */
    barcode: {
      serialNumber: '20195299999',
      softwareVersion: '5.15.0',
      urCapVersion: '2.8.0',
      firmwareVersion: '4.67.0',
      manufacturer: 'Universal Robots',
      model: 'UR10',
      threePositionEnabled: false,
    },
    /**
     * Simulated wifi data when connect to a Direct Connect machine.
     * Allows you to directly connect to a machine when you're on the same
     * network but not connected to it via Beacon Wi-Fi.
     */
    wifi: {
      /**
       * While on the same network (e.g. VPN or Office Network), run:
       * ```sh
       * SERIAL_NUMBER="20195299999"
       * IPv4="$(dig +short -4 ur-${SERIAL_NUMBER})"
       * ```
       */
      ipAddress: '127.0.0.1',
      /**
       * While on the same network (e.g. VPN or Office Network), run:
       * ```sh
       * ssh root@${IPv4} "ip route | grep default | awk '{print \$3}'"
       * ```
       */
      gateway: '127.0.0.1',
    },
  },
  support: {
    email: 'support@hirebotics.com',
  },
};

const KioskConfig: Partial<ConfigSchema> = {
  robot: {
    network: {
      // Empty string = use window.location.origin (Docker/remote), or specify explicit host
      host: import.meta.env.VITE_KIOSK_NETWORK_HOST ?? '127.0.0.1',
    },
    api: {
      // be sure to include the slash at the end since the construction of the
      // urls using new URL will truncate the last part of the path if it
      // doesn't end with a slash
      path:
        import.meta.env.VITE_KIOSK_API_PATH ??
        'hirebotics/beacon/beacon-backend/beacon-api/',
      port: Number(import.meta.env.VITE_KIOSK_NETWORK_PORT ?? 80),
    },
    rpc: {
      // Empty string = use window.location.origin (Docker/remote), or specify explicit host
      host: import.meta.env.VITE_KIOSK_RPC_HOST ?? '127.0.0.1',
      path:
        import.meta.env.VITE_KIOSK_RPC_PATH ??
        'hirebotics/beacon/beacon-backend/beacon-rpc-api/RPC2',
      port: Number(import.meta.env.VITE_KIOSK_RPC_PORT ?? 80),
    },
  },
};

const ApplicationConfig = ((): ConfigSchema => {
  const config = cloneDeep(BaseApplicationConfig);

  if (config.kiosk.enabled) {
    return merge({}, BaseApplicationConfig, KioskConfig);
  } else {
    return config;
  }
})();

const isLocalDev = () => {
  return ApplicationConfig.env === 'local' || ApplicationConfig.env === 'e2e';
};

export { ApplicationConfig, isLocalDev };
